/**
 * CEO Strategy Dashboard panels — iter-82 → iter-83 (Feb 2026).
 *
 * Six real-time tiles + a shared USA State / Big City / Region filter bar
 * powering the CEO home page:
 *
 *   1. SalesLeaderboardPanel — Leads Captured (30d + all-time) + Contracts.
 *   2. RiskAssessmentPanel — Red/Amber/Green flag per property.
 *   3. GrowthProjectionPanel — Editable growth-rate slider + milestones.
 *   4. FinanceScorecardPanel — Outstanding, Overdue, MTD collected + top payers.
 *   5. SafetyRiskPanel — Open incidents, 30/90-day trend, training %, hotspots.
 *   6. HrLeadershipPanel — Headcount, hires 30/90d, open positions, turnover.
 *
 * All 6 panels are driven by the same shared `<GeoFilterProvider>` at the top
 * so a single State/City/Region selection re-scopes every tile in unison.
 */
import { useEffect, useMemo, useState, createContext, useContext, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  TrendingUp, Users, ShieldAlert, Award, Sparkles, ChevronRight, Trophy,
  DollarSign, HeartPulse, UserPlus, MapPin, X, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════════ */
/* GEO FILTER CONTEXT + BAR                                            */
/* ═══════════════════════════════════════════════════════════════════ */
const GeoFilterContext = createContext(null);

export function GeoFilterProvider({ children, boardFilter }) {
  // Iter-105 · Item 1 — `GeoFilterProvider` now bridges to whichever
  // board's `useBoardFilter()` instance is passed in via prop. Every
  // panel that reads `useGeoFilter()` re-queries when this board's
  // scope changes — and only this board's scope.
  const unified = boardFilter || {};
  const [meta, setMeta] = useState({ states: [], by_state: [], regions_total: 0 });

  useEffect(() => {
    fetch(`${API}/api/executive/geo-filters`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setMeta(d))
      .catch(() => {});
  }, []);

  const state  = unified.state  || '';
  const city   = unified.city   || '';
  const region = unified.region || '';

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (state) p.set('state', state);
    if (city) p.set('city', city);
    if (region) p.set('region', region);
    if (unified.date_from) p.set('date_from', unified.date_from);
    if (unified.date_to)   p.set('date_to',   unified.date_to);
    if (unified.windowDays)p.set('window_days', String(unified.windowDays));
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [state, city, region, unified.date_from, unified.date_to, unified.windowDays]);

  const reset = useCallback(() => { unified.clear && unified.clear(); }, [unified]);

  const value = useMemo(() => ({
    state, city, region, meta, query, reset,
    windowDays: unified.windowDays || 30,
    refreshTick: unified.refreshTick || 0,
    setState: (v) => unified.setFilter && unified.setFilter({ state: v, city: null, region: null }),
    setCity:  (v) => unified.setFilter && unified.setFilter({ city: v || null, region: null }),
    setRegion:(v) => unified.setFilter && unified.setFilter({ region: v || null }),
  }), [state, city, region, meta, query, reset, unified]);

  return <GeoFilterContext.Provider value={value}>{children}</GeoFilterContext.Provider>;
}

export function useGeoFilter() {
  return useContext(GeoFilterContext) || { state: '', city: '', region: '', meta: { states: [], by_state: [] }, query: '' };
}

/**
 * USA-wide State → Big City → Region cascade bar.
 *
 * Iter-104 · Item 2/8 — Deprecated. Every admin dashboard now renders
 * the shared Unified Filter (via `DashboardShell`), which drives every
 * board (including CEO Board panels) through the `GeoFilterProvider`
 * bridge above. This function returns `null` so legacy callsites keep
 * working without rendering a duplicate filter bar.
 */
export function GeoFilterBar() {
  return null;
}

/** @deprecated — see GeoFilterBar note. */
function GeoFilterBarLegacy() {
  const { state, city, region, meta, setState, setCity, setRegion, reset } = useGeoFilter();
  const stateBlock = (meta.by_state || []).find((b) => b.state === state);
  const bigCities = stateBlock?.big_cities || [];
  const regionsForState = stateBlock?.regions || [];
  const regionsForCity = city ? regionsForState.filter((r) => r.big_city === city) : regionsForState;
  const active = state || city || region;

  return (
    <Card className="p-3 bg-zinc-900 border-zinc-700 mb-4" data-testid="ceo-geo-filter-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-[#FFD60A] font-bold flex items-center gap-1">
          <MapPin className="h-3 w-3" /> USA Filter
        </span>

        <FilterSelect
          label="State"
          value={state}
          options={[{ value: '', label: 'All US States' }, ...(meta.states || []).map((s) => ({ value: s, label: s }))]}
          onChange={setState}
          testid="ceo-filter-state"
        />

        <FilterSelect
          label="Big City"
          value={city}
          disabled={!state}
          options={[{ value: '', label: state ? 'All Big Cities' : 'Select state first' }, ...bigCities.map((c) => ({ value: c, label: c }))]}
          onChange={setCity}
          testid="ceo-filter-city"
        />

        <FilterSelect
          label="Region"
          value={region}
          disabled={!state}
          options={[
            { value: '', label: state ? 'All Regions' : 'Select state first' },
            ...regionsForCity.map((r) => ({ value: r.region_id || r.region_code, label: `${r.region_code} · ${r.name}` })),
          ]}
          onChange={setRegion}
          testid="ceo-filter-region"
        />

        {active && (
          <button
            type="button"
            onClick={reset}
            className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded-full border border-zinc-700"
            data-testid="ceo-filter-reset"
          >
            <X className="h-3 w-3" /> Reset
          </button>
        )}

        <span className="ml-auto text-[10px] text-zinc-500">
          {meta.regions_total} regions · {(meta.states || []).length} states seeded
        </span>
      </div>

      {active && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-zinc-500">Filtering:</span>
          {state && <Badge className="bg-emerald-700 text-white text-[10px]">STATE · {state}</Badge>}
          {city && <Badge className="bg-sky-700 text-white text-[10px]">BIG CITY · {city}</Badge>}
          {region && <Badge className="bg-amber-700 text-white text-[10px]">REGION · {region}</Badge>}
        </div>
      )}
    </Card>
  );
}

function FilterSelect({ label, value, options, onChange, disabled, testid }) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-zinc-300">
      <span className="uppercase tracking-widest text-[9px] text-zinc-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="bg-zinc-800 border border-zinc-700 text-white text-xs rounded px-2 py-1 disabled:opacity-40"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value || 'all'} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 1. SALES LEADERBOARD                                                */
/* ═══════════════════════════════════════════════════════════════════ */
export function SalesLeaderboardPanel() {
  const { query } = useGeoFilter();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/executive/leaderboard${query}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [query]);

  return (
    <section data-testid="ceo-sales-leaderboard">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] font-semibold flex items-center gap-2">
          <Trophy className="h-4 w-4" /> Sales Leaderboard
        </h2>
        <span className="text-[11px] text-zinc-500">Real-time · updates on refresh</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Leads Captured · 30d"
          value={data?.leads_captured_30d ?? '—'}
          sub={`${data?.leads_captured_total ?? 0} lifetime`}
          tone="#FFD60A"
          icon={<Users className="h-5 w-5" />}
          testid="sales-leaderboard-leads-30d"
        />
        <StatTile
          label="Chat Leads · 30d"
          value={data?.chat_leads_30d ?? '—'}
          sub="From Noble widget"
          tone="#14b8a6"
          icon={<Sparkles className="h-5 w-5" />}
          testid="sales-leaderboard-chat-leads"
        />
        <StatTile
          label="Contracts Pending"
          value={data?.contracts_pending ?? '—'}
          sub={`${data?.contracts_signed ?? 0} active`}
          tone="#f97316"
          icon={<Award className="h-5 w-5" />}
          testid="sales-leaderboard-contracts-pending"
        />
        <StatTile
          label="Hot Leads"
          value={data?.hot_leads ?? '—'}
          sub="In pipeline"
          tone="#ef4444"
          icon={<TrendingUp className="h-5 w-5" />}
          testid="sales-leaderboard-hot-leads"
        />
      </div>

      {data?.top_reps?.length > 0 && (
        <Card className="mt-3 p-3 bg-zinc-900 border-zinc-700" data-testid="sales-leaderboard-top-reps">
          <p className="text-[10px] uppercase tracking-widest text-[#FFD60A] font-bold mb-2">
            🏆 Top Reps · Last 30 days
          </p>
          <div className="space-y-1.5">
            {data.top_reps.map((r, i) => (
              <div key={r.user_id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-200">
                  <span className="text-[#FFD60A] font-bold">#{i + 1}</span> · {r.name}
                </span>
                <Badge className="bg-emerald-700 text-white text-[10px]">{r.leads} leads</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 2. RISK ASSESSMENT PANEL                                            */
/* ═══════════════════════════════════════════════════════════════════ */
export function RiskAssessmentPanel() {
  const { query } = useGeoFilter();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/executive/risk${query}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [query]);

  const summary = data?.summary || { red: 0, amber: 0, green: 0, total: 0 };
  const reds = (data?.properties || []).filter((p) => p.flag === 'red').slice(0, 6);
  const ambers = (data?.properties || []).filter((p) => p.flag === 'amber').slice(0, 6);

  return (
    <section data-testid="ceo-risk-panel">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-red-400 font-semibold flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> Risk Assessment
        </h2>
        <span className="text-[11px] text-zinc-500">
          {summary.total} properties · combined service-failure + compliance score
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <RiskTile flag="red" value={summary.red} label="High Risk" />
        <RiskTile flag="amber" value={summary.amber} label="Watchlist" />
        <RiskTile flag="green" value={summary.green} label="Healthy" />
      </div>
      {reds.length + ambers.length > 0 && (
        <Card className="p-3 bg-zinc-900 border-zinc-700" data-testid="ceo-risk-detail">
          {reds.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-1">
                🚨 High-Risk Properties
              </p>
              <div className="space-y-1 mb-3">
                {reds.map((p) => <RiskRow key={p.property_id} p={p} />)}
              </div>
            </>
          )}
          {ambers.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">
                ⚠ Watchlist
              </p>
              <div className="space-y-1">
                {ambers.map((p) => <RiskRow key={p.property_id} p={p} />)}
              </div>
            </>
          )}
        </Card>
      )}
      {summary.total > 0 && summary.red === 0 && summary.amber === 0 && (
        <Card className="p-4 bg-emerald-900/30 border-emerald-700" data-testid="ceo-risk-all-green">
          <p className="text-sm text-emerald-300 font-semibold">
            ✅ All {summary.total} properties are healthy — no reds or ambers flagged.
          </p>
        </Card>
      )}
    </section>
  );
}

function RiskRow({ p }) {
  return (
    <div className="flex items-center justify-between text-xs text-zinc-300 gap-2">
      <span className="truncate flex-1">
        {p.name} <span className="text-zinc-500">· {p.city || '—'}, {p.state || '—'}</span>
      </span>
      <span className="text-zinc-500 whitespace-nowrap">
        {p.service_failures_30d} fails · {p.open_violations} viol · {p.compliance_score}% CS
      </span>
      <ChevronRight className="h-3 w-3 text-zinc-500 flex-shrink-0" />
    </div>
  );
}

function RiskTile({ flag, value, label }) {
  const palette = {
    red:   { bg: 'rgba(239,68,68,0.12)',  border: '#ef4444', text: '#fca5a5' },
    amber: { bg: 'rgba(251,191,36,0.12)', border: '#f59e0b', text: '#fcd34d' },
    green: { bg: 'rgba(16,185,129,0.12)', border: '#10b981', text: '#6ee7b7' },
  }[flag];
  return (
    <div
      className="rounded-xl p-4 border-2"
      style={{ backgroundColor: palette.bg, borderColor: palette.border }}
      data-testid={`ceo-risk-tile-${flag}`}
    >
      <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: palette.text }}>
        {label}
      </p>
      <p className="text-3xl font-black" style={{ color: palette.border }}>{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 3. GROWTH PROJECTION                                                */
/* ═══════════════════════════════════════════════════════════════════ */
export function GrowthProjectionPanel() {
  const { query } = useGeoFilter();
  const [growth, setGrowth] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (pct) => {
    setLoading(true);
    try {
      const sep = query ? '&' : '?';
      const res = await fetch(`${API}/api/executive/growth-projection${query}${sep}monthly_growth_pct=${pct}&horizon_months=12`, {
        credentials: 'include',
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(growth); }, [query]);

  // Debounce slider changes — 250ms after last drag
  useEffect(() => {
    const t = setTimeout(() => load(growth), 250);
    return () => clearTimeout(t);
  }, [growth]);

  const points = useMemo(() => {
    if (!data?.series?.length) return '';
    const max = Math.max(...data.series.map((s) => s.units), 1);
    const w = 300;
    const h = 90;
    return data.series.map((s, i) => {
      const x = (i / (data.series.length - 1)) * w;
      const y = h - (s.units / max) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }, [data]);

  return (
    <section data-testid="ceo-growth-projection">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Growth Projection · 12 months
        </h2>
        <span className="text-[11px] text-zinc-500">
          {loading ? 'Recalculating…' : `Projected from ${data?.current_units?.toLocaleString() || 0} units`}
        </span>
      </div>
      <Card className="p-4 bg-zinc-900 border-zinc-700">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Slider + milestones */}
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label htmlFor="growth-slider" className="text-xs text-zinc-300 font-semibold">
                Monthly growth target
              </label>
              <span className="text-2xl font-black text-emerald-400" data-testid="ceo-growth-value">
                {growth.toFixed(1)}<span className="text-sm">%</span>
              </span>
            </div>
            <input
              id="growth-slider"
              type="range"
              min={0}
              max={25}
              step={0.5}
              value={growth}
              onChange={(e) => setGrowth(parseFloat(e.target.value))}
              className="w-full accent-emerald-500"
              data-testid="ceo-growth-slider"
            />
            <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
              <span>0%</span>
              <span>Conservative 5%</span>
              <span>Aggressive 15%</span>
              <span>25%</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <MilestoneTile label="3-mo" value={data?.milestones?.['3_month']} testid="growth-milestone-3" />
              <MilestoneTile label="6-mo" value={data?.milestones?.['6_month']} testid="growth-milestone-6" />
              <MilestoneTile label="12-mo" value={data?.milestones?.['12_month']} testid="growth-milestone-12" />
            </div>

            <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
              Baseline: {data?.current_units?.toLocaleString() || 0} units across {data?.signed_properties || 0} signed
              properties + {data?.pipeline_units?.toLocaleString() || 0} units in the hot-leads pipeline (30% conversion
              blended evenly across the 12-month horizon).
            </p>
          </div>

          {/* Sparkline */}
          <div className="flex items-center justify-center">
            <svg viewBox="0 0 300 100" width="100%" height="100" className="text-emerald-400" aria-label="Growth sparkline">
              <defs>
                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(52,211,153)" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="rgb(52,211,153)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {points && (
                <>
                  <polyline
                    fill="url(#growthGrad)"
                    stroke="none"
                    points={`0,90 ${points} 300,90`}
                  />
                  <polyline
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={points}
                  />
                </>
              )}
            </svg>
          </div>
        </div>
      </Card>
    </section>
  );
}

function MilestoneTile({ label, value, testid }) {
  return (
    <div className="rounded-lg p-2 bg-zinc-800 border border-zinc-700 text-center" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest text-zinc-400">{label}</p>
      <p className="text-lg font-black text-emerald-400">{value?.toLocaleString() || '—'}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* Shared stat tile                                                    */
/* ═══════════════════════════════════════════════════════════════════ */
function StatTile({ label, value, sub, tone, icon, testid }) {
  return (
    <Card
      className="p-4 bg-zinc-900 border-2"
      style={{ borderColor: `${tone}55` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="p-1.5 rounded-lg" style={{ backgroundColor: `${tone}22`, color: tone }}>
          {icon}
        </span>
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</p>
      </div>
      <p className="text-3xl font-black text-white leading-none">{value}</p>
      <p className="text-[10px] text-zinc-500 mt-1">{sub}</p>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 4. FINANCE STATUS                                                   */
/* ═══════════════════════════════════════════════════════════════════ */
function fmtMoney(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

export function FinanceScorecardPanel() {
  const { query } = useGeoFilter();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/executive/finance-scorecard${query}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [query]);

  return (
    <section data-testid="ceo-finance-scorecard">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold flex items-center gap-2">
          <DollarSign className="h-4 w-4" /> Finance Status
        </h2>
        <span className="text-[11px] text-zinc-500">
          {data?.invoices_open ?? 0} open invoices · {data?.invoices_paid_30d ?? 0} paid last 30d
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Outstanding"
          value={fmtMoney(data?.outstanding_amount)}
          sub={`${data?.invoices_open ?? 0} invoices open`}
          tone="#f59e0b"
          icon={<DollarSign className="h-5 w-5" />}
          testid="finance-outstanding"
        />
        <StatTile
          label="Overdue"
          value={fmtMoney(data?.overdue_amount)}
          sub="Past due date"
          tone="#ef4444"
          icon={<TrendingUp className="h-5 w-5" />}
          testid="finance-overdue"
        />
        <StatTile
          label="Collected · MTD"
          value={fmtMoney(data?.collected_mtd)}
          sub="Month-to-date"
          tone="#10b981"
          icon={<Award className="h-5 w-5" />}
          testid="finance-collected-mtd"
        />
        <StatTile
          label="Paid · 30d"
          value={data?.invoices_paid_30d ?? '—'}
          sub="Invoices settled"
          tone="#14b8a6"
          icon={<Trophy className="h-5 w-5" />}
          testid="finance-paid-30d"
        />
      </div>
      {data?.top_payers?.length > 0 && (
        <Card className="mt-3 p-3 bg-zinc-900 border-zinc-700" data-testid="finance-top-payers">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">🏦 Top Payers · Lifetime</p>
          <div className="space-y-1.5">
            {data.top_payers.map((p, i) => (
              <div key={p.property_id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-200"><span className="text-emerald-400 font-bold">#{i + 1}</span> · {p.name} <span className="text-zinc-500 text-xs">· {p.city}, {p.state}</span></span>
                <Badge className="bg-emerald-800 text-white text-[10px]">{fmtMoney(p.paid_total)}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 5. SAFETY & RISK MANAGEMENT                                         */
/* ═══════════════════════════════════════════════════════════════════ */
export function SafetyRiskPanel() {
  const { query } = useGeoFilter();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/executive/safety-scorecard${query}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [query]);

  return (
    <section data-testid="ceo-safety-scorecard">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-orange-400 font-semibold flex items-center gap-2">
          <HeartPulse className="h-4 w-4" /> Safety & Risk Management
        </h2>
        <span className="text-[11px] text-zinc-500">
          {data?.active_field_staff ?? 0} active field staff · Training {data?.training_compliance_pct ?? 0}%
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Open Incidents"
          value={data?.open_incidents ?? '—'}
          sub="Not yet resolved"
          tone="#ef4444"
          icon={<ShieldAlert className="h-5 w-5" />}
          testid="safety-open-incidents"
        />
        <StatTile
          label="Incidents · 30d"
          value={data?.incidents_30d ?? '—'}
          sub={`${data?.incidents_90d ?? 0} in last 90d`}
          tone="#f59e0b"
          icon={<TrendingUp className="h-5 w-5" />}
          testid="safety-incidents-30d"
        />
        <StatTile
          label="Training %"
          value={`${data?.training_compliance_pct ?? 0}%`}
          sub="Field staff certified"
          tone="#10b981"
          icon={<Award className="h-5 w-5" />}
          testid="safety-training-pct"
        />
        <StatTile
          label="Active Staff"
          value={data?.active_field_staff ?? '—'}
          sub="On payroll"
          tone="#14b8a6"
          icon={<Users className="h-5 w-5" />}
          testid="safety-active-staff"
        />
      </div>
      {data?.hotspots?.length > 0 && (
        <Card className="mt-3 p-3 bg-zinc-900 border-zinc-700" data-testid="safety-hotspots">
          <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">🔥 Incident Hotspots · Last 90 days</p>
          <div className="space-y-1.5">
            {data.hotspots.map((h) => (
              <div key={h.property_id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-200">{h.name} <span className="text-zinc-500 text-xs">· {h.city}, {h.state}</span></span>
                <Badge className="bg-red-700 text-white text-[10px]">{h.incidents_90d} incidents</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */
/* 6. HR LEADING INDICATORS                                            */
/* ═══════════════════════════════════════════════════════════════════ */
export function HrLeadershipPanel() {
  const { query } = useGeoFilter();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/executive/hr-scorecard${query}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [query]);

  return (
    <section data-testid="ceo-hr-scorecard">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-widest text-sky-400 font-semibold flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> HR · Hiring & Retention
        </h2>
        <span className="text-[11px] text-zinc-500">
          {data?.active_headcount ?? 0} active headcount · {data?.open_positions ?? 0} open roles
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Active Headcount"
          value={data?.active_headcount ?? '—'}
          sub="Full-time equivalents"
          tone="#0ea5e9"
          icon={<Users className="h-5 w-5" />}
          testid="hr-active-headcount"
        />
        <StatTile
          label="Hires · 30d"
          value={data?.hires_30d ?? '—'}
          sub={`${data?.hires_90d ?? 0} in last 90d`}
          tone="#10b981"
          icon={<UserPlus className="h-5 w-5" />}
          testid="hr-hires-30d"
        />
        <StatTile
          label="Open Positions"
          value={data?.open_positions ?? '—'}
          sub="Actively hiring"
          tone="#FFD60A"
          icon={<Sparkles className="h-5 w-5" />}
          testid="hr-open-positions"
        />
        <StatTile
          label="Turnover · Annualized"
          value={`${data?.turnover_annualized_pct ?? 0}%`}
          sub={`${data?.terminations_30d ?? 0} exits last 30d`}
          tone="#ef4444"
          icon={<TrendingUp className="h-5 w-5" />}
          testid="hr-turnover"
        />
      </div>
      {data?.role_breakdown?.length > 0 && (
        <Card className="mt-3 p-3 bg-zinc-900 border-zinc-700" data-testid="hr-role-breakdown">
          <p className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-2">👥 Headcount by Role</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.role_breakdown.map((r) => (
              <div key={r.role} className="rounded-md bg-zinc-800 border border-zinc-700 p-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400">{r.role.replaceAll('_', ' ')}</p>
                <p className="text-xl font-black text-sky-400">{r.count}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

